import { PushNotifications } from '@capacitor/push-notifications'
import { isNative } from './platform'
import { useNotifyPromptStore } from '../store/notifyPromptStore'
import type { AndroidPushSubscription } from '../api/push'

export const nativeBoundUserKey = 'humanteq-native-push-bound-user'
export const nativeTokenKey = 'humanteq-native-push-token'
export const webDisabledKey = (userId: string) => `humanteq-web-push-disabled:${userId}`

type DeviceSubscription = PushSubscriptionJSON | AndroidPushSubscription
type RemoveDevice = (subscription: DeviceSubscription) => Promise<unknown>

export function isPushDeviceDisabled(userId: string) {
  return isNative
    ? useNotifyPromptStore.getState().isNativeDismissed(userId)
    : localStorage.getItem(webDisabledKey(userId)) === '1'
}

export function clearPushDeviceDisabled(userId: string) {
  if (isNative) useNotifyPromptStore.getState().clearNativeDismissal(userId)
  else localStorage.removeItem(webDisabledKey(userId))
}

function markPushDeviceDisabled(userId: string) {
  if (isNative) useNotifyPromptStore.getState().dismissNative(userId)
  else localStorage.setItem(webDisabledKey(userId), '1')
}

export async function disablePushDevice(
  userId: string,
  removeDevice?: RemoveDevice,
  {
    rollbackOnFailure = true,
    awaitBackend = true,
  }: { rollbackOnFailure?: boolean; awaitBackend?: boolean } = {},
) {
  // Set this before the first await so a concurrent startup refresh cannot
  // re-register the device while unsubscribe is in progress.
  markPushDeviceDisabled(userId)

  if (isNative) {
    const token = localStorage.getItem(nativeBoundUserKey) === userId
      ? localStorage.getItem(nativeTokenKey)
      : null
    let nativeDisabled = false
    const backendAttempt = token && removeDevice
      ? removeDevice({ platform: 'android', token }).then(() => true, () => false)
      : Promise.resolve(false)
    try {
      await PushNotifications.unregister()
      nativeDisabled = true
    } catch {
      /* exact backend removal below may still disable delivery */
    }
    const backendDisabled = awaitBackend ? await backendAttempt : false
    const disabled = nativeDisabled || backendDisabled
    if (disabled) {
      localStorage.removeItem(nativeBoundUserKey)
      localStorage.removeItem(nativeTokenKey)
    } else if (rollbackOnFailure) {
      clearPushDeviceDisabled(userId)
    }
    return disabled
  }

  try {
    // `ready` never settles when this browser has no registered worker (for
    // example a normal Vite tab), which must not make logout hang forever.
    const registration = navigator.serviceWorker.getRegistration
      ? await navigator.serviceWorker.getRegistration()
      : await navigator.serviceWorker.ready
    if (!registration) return true
    const subscription = await registration.pushManager.getSubscription()
    if (!subscription) return true

    const backendAttempt = removeDevice
      ? removeDevice(subscription.toJSON()).then(() => true, () => false)
      : Promise.resolve(false)
    let browserDisabled = false
    try {
      browserDisabled = await subscription.unsubscribe()
    } catch {
      /* backend removal above may already have disabled delivery */
    }
    const backendDisabled = awaitBackend ? await backendAttempt : false
    const disabled = backendDisabled || browserDisabled
    if (!disabled && rollbackOnFailure) clearPushDeviceDisabled(userId)
    return disabled
  } catch {
    if (rollbackOnFailure) clearPushDeviceDisabled(userId)
    return false
  }
}
